import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import { createGlobalStyle } from "styled-components";
import { AuthUserDto } from "types/api/auth";
import { MailboxResponseDto } from "types/api/mailbox";
import ToastContainer from "../components/ToastContainer/ToastContainer";
import { getProfile, setAuthenticated } from "../reduxFolder/reducers/auth";
import { getMailboxes } from "../reduxFolder/reducers/mailbox";
import {
  authSelectAuthenticated,
  authSelectLocale,
} from "../reduxFolder/selectors/globalSelectors";
import { globalStyles } from "../styles/styles";
import { getSupportedLocale } from "../translations/i18nConfig";
import { clearAuthData, getAuthData } from "../utils/authData";

// Mounted in src/index.jsx above the router rather than here: on a route
// render error the router swaps this whole tree for RouteErrorScreen, and
// styled-components removes a GlobalStyle when it unmounts.
export const GlobalStyle = createGlobalStyle`
  ${({ theme }) => globalStyles(theme)}
`;

interface AppProps {
  currentLocale: string;
  authenticated: boolean | null;
  getProfile: () => Promise<AuthUserDto>;
  setAuthenticated: (isAuthenticated: boolean) => void;
  getMailboxes: () => Promise<MailboxResponseDto>;
}

export const App: FC<AppProps> = React.memo(
  ({
    currentLocale,
    authenticated,
    getProfile,
    setAuthenticated,
    getMailboxes,
  }) => {
    const { i18n } = useTranslation();
    const [locale, setLocale] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
      // The /impersonate route owns auth on load — it swaps in the impersonation
      // token and fetches the profile itself. Firing the boot getProfile here too
      // races that swap, and a 401 from the leftover token hard-redirects to /signin.
      if (window.location.pathname.startsWith("/impersonate")) return;

      const authData = getAuthData();
      const token = authData?.["access-token" as keyof typeof authData] ?? null;

      if (!token) {
        setAuthenticated(false);
        return;
      }

      getProfile().catch(async () => {
        await clearAuthData();
        navigate("/signin");
      });
    }, [getProfile, setAuthenticated, navigate]);

    // Warehouse addresses for /mailbox and the Storage demo's CTA. Sign-in,
    // sign-up and the boot profile fetch all set `authenticated`, so keying on it
    // covers every entry path — and fetches again after a logout wiped the store.
    // Failures are logged, not toasted: both consumers retry on mount.
    useEffect(() => {
      if (authenticated !== true) return;

      getMailboxes().catch((error) =>
        console.error("Failed to prefetch mailboxes", error),
      );
    }, [authenticated, getMailboxes]);

    useEffect(() => {
      // If a token exists, the profile (with its saved locale) is on the way —
      // wait for it instead of flashing the navigator locale in the meantime.
      const authData = getAuthData();
      const hasToken =
        !!authData?.["access-token" as keyof typeof authData];
      if (hasToken && !currentLocale) return;

      const appLocale = getSupportedLocale(currentLocale);

      if (appLocale !== locale) {
        i18n.changeLanguage(appLocale);
        setLocale(appLocale);
      }
    }, [currentLocale, setLocale, i18n, locale]);

    return (
      <>
        <Outlet />
        <ToastContainer />
      </>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    currentLocale: authSelectLocale,
    authenticated: authSelectAuthenticated,
  }),
  { getProfile, setAuthenticated, getMailboxes },
);

export default withConnect(App);
